/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'
import {useIntl} from 'react-intl'

// Components
import {
    Breadcrumb as ChakraBreadcrumb,
    BreadcrumbItem as ChakraBreadcrumbItem,
    BreadcrumbLink as ChakraBreadcrumbLink,
    // Hooks
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {HOME_BREADCRUMB, HOME_HREF} from '../../constants'
import SEOStructuredData from '../seo-structured-data'

// Others
import {categoryUrlBuilder} from '../../utils/url'
import useMultiSite from '../../hooks/use-multi-site'
import {HEADER_STYLE_VARIANTS} from '../header/constants'

/**
 * Breadcrumb component for rendering a navigational breadcrumb trail.
 *
 * This component displays a breadcrumb navigation bar that reflects the user's current location
 * within the site hierarchy. It supports categories, product detail pages (PDP), and custom page types.
 * The breadcrumb always starts with a link to the homepage, followed by category links, and optionally
 * a product link if on a PDP. It also injects SEO structured data for breadcrumbs.
 *
 * The component is responsive and automatically scrolls to the end of the breadcrumb list on smaller screens.
 * It adapts its style based on the current page's header style and supports theming via Chakra UI.
 *
 * Props:
 * @param {Array} categories - List of category objects representing the navigation path.
 * @param {Object} product - Product object for PDP breadcrumbs (optional).
 * @param {string} type - Type of breadcrumb (e.g., 'breadcrumb', 'stores', 'content').
 * @param {Object} pageData - Additional data about the current page, including header style.
 * @param {ReactNode} children - Additional breadcrumb items or custom elements.
 * @param {...Object} rest - Additional props passed to the ChakraBreadcrumb component.
 *
 * Usage:
 * - Renders homepage as the first breadcrumb.
 * - Renders category breadcrumbs, with the last one optionally marked as the current page.
 * - Renders product breadcrumb if a product is provided.
 * - Accepts custom children for special use cases (e.g., stores or content pages).
 *
 * Accessibility:
 * - Uses semantic HTML for breadcrumbs.
 * - Highlights the current page in the breadcrumb trail.
 *
 * SEO:
 * - Injects structured data for breadcrumbs to improve search engine visibility.
 */
const Breadcrumb = ({categories, product, type, pageData, children, ...rest}) => {
    const intl = useIntl()
    const {buildUrl} = useMultiSite()
    const isPDP = Boolean(product)
    const breadcrumbRef = useRef(null)
    const shouldScrollToEnd = useBreakpointValue({base: true, sm: true, md: true, lg: false})
    const headerStyle = pageData?.currentPage?.data?.headerStyle
    const headerStyleVariant = HEADER_STYLE_VARIANTS[headerStyle] || ''
    const styles = useMultiStyleConfig('Breadcrumb', {variant: headerStyleVariant})

    useEffect(() => {
        if (breadcrumbRef.current && shouldScrollToEnd) {
            const breadcrumbList = breadcrumbRef.current.querySelector('ol')

            if (breadcrumbList) {
                breadcrumbList.scrollTo({
                    left: breadcrumbList.scrollWidth,
                    behavior: 'smooth'
                })
            }
        }
    }, [shouldScrollToEnd])

    return (
        <>
            <SEOStructuredData
                data={{
                    categories,
                    product,
                    ...pageData
                }}
                type={type || 'breadcrumb'}
            ></SEOStructuredData>
            <ChakraBreadcrumb
                ref={breadcrumbRef}
                className="sf-breadcrumb"
                variant={headerStyleVariant}
                separator={'/'}
                spacing={'3'}
                {...styles.container}
                {...rest}
            >
                {/* The first breadcrumb is always homepage */}
                <ChakraBreadcrumbItem key="home">
                    <ChakraBreadcrumbLink as={RouteLink} to={buildUrl(HOME_HREF)} {...styles.link}>
                        {intl.formatMessage(HOME_BREADCRUMB)}
                    </ChakraBreadcrumbLink>
                </ChakraBreadcrumbItem>

                {/* CLP / PDP breadcrumbs */}
                {categories?.map((category, index) => {
                    const isLastCategory = !product && index === categories.length - 1

                    return (
                        <ChakraBreadcrumbItem
                            key={category.id}
                            data-testid="sf-crumb-item"
                            isCurrentPage={isLastCategory}
                        >
                            {isLastCategory ? (
                                <ChakraBreadcrumbLink {...styles.lastitem}>
                                    {isPDP
                                        ? category?.displayName
                                            ? category?.displayName
                                            : category.name
                                        : category?.c_seoDisplayName || category?.name}
                                </ChakraBreadcrumbLink>
                            ) : (
                                <ChakraBreadcrumbLink
                                    as={RouteLink}
                                    to={buildUrl(categoryUrlBuilder(category))}
                                    sx={styles.link}
                                >
                                    {isPDP
                                        ? category?.displayName
                                            ? category?.displayName
                                            : category.name
                                        : category?.c_seoDisplayName || category?.name}
                                </ChakraBreadcrumbLink>
                            )}
                        </ChakraBreadcrumbItem>
                    )
                })}
                {product && (
                    <ChakraBreadcrumbItem
                        key={product.id}
                        data-testid="sf-crumb-item"
                        isCurrentPage
                    >
                        <ChakraBreadcrumbLink {...styles.lastitem}>
                            {product.name}
                        </ChakraBreadcrumbLink>
                    </ChakraBreadcrumbItem>
                )}

                {/* Use cases:
                    type = 'stores' - Stores page breadcrumbs,
                    type = 'content' - Content page breadcrumbs
                */}
                {children}
            </ChakraBreadcrumb>
        </>
    )
}

Breadcrumb.displayName = 'Breadcrumb'

Breadcrumb.propTypes = {
    /**
     * The categories to be displayed in this breadcrumb.
     */
    categories: PropTypes.array,
    product: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string
    }),
    type: PropTypes.string,
    pageData: PropTypes.object,
    children: PropTypes.node
}

export default Breadcrumb
